<!--
  File name: ProvideDetailsModal.vue
  Program Description:
    - This component displays the details of items tagged as "No information yet" or "No."
-->
<template>
  <v-dialog
    v-if="[1,3].includes(activeTab) && hasNoInfoYet"
    v-model="modal"
    max-width="1000px"
    :persistent="!disabled"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-card
        v-show="activeTab === 3 && hasNoInfoYet"
        v-bind="attrs"
        :color="`${isSubmitted ? 'primary' : 'rgb(210,210,210)'}`"
        flat
        v-on="on"
      >
        <v-card-title :style="`color:${isSubmitted ? 'white' : 'rgb(50,50,50)'}`">
          Provide details on items tagged as "No" and "No Information yet" on the data breach notification details here. {{ isSubmitted ? ' - Submitted' : '' }}
          <v-spacer />
          <v-icon
            v-show="isSubmitted"
            color="white"
            mdi-check-circle
          >
            mdi-check-circle
          </v-icon>
        </v-card-title>
      </v-card>
    </template>
    <v-card
      class="pa-9"
    >
      <v-card-title class="primary--text text-h6 justify-center">
        Comply with Initial Order
      </v-card-title>
      <v-form
        ref="form"
        @submit.prevent="confirm"
      >
        <v-container>
          <template v-for="item, idx in noInfoYet">
            <v-row :key="item.label">
              <v-col>
                <h3 style="color: rgb(152,152,152)">
                  {{ item.label }}
                </h3>
              </v-col>
            </v-row>
            <v-row :key="idx">
              <v-col cols="4">
                <v-autocomplete
                  :value="item.provided"
                  :items="['No information yet', 'No']"
                  :outlined="!disabled"
                  label="Sufficiently provided"
                  class="required-field"
                  :class="item.provided === 'No information yet' ? 'highlight' : ''"
                  disabled
                  readonly
                />
              </v-col>
              <v-col>
                <v-textarea
                  label="Provide Details"
                  :value="initialVal(item.model)"
                  :name="item.model"
                  :outlined="!disabled"
                  :disabled="disabled"
                  :readonly="disabled"
                  :rules="[v => !!v || `Provide Details is required`]"
                  class="cursor"
                  dense
                  rows="3"
                />
              </v-col>
            </v-row>
          </template>
          <v-row>
            <v-col>
              <h3 style="color: rgb(152,152,152)">
                Initial Order
              </h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-combobox
                label="Initial Order"
                chips
                disabled
                prepend-icon="mdi-paperclip"
                append-icon=""
                :value="attachmentName"
              />
            </v-col>
            <v-col
              cols="1"
              class="d-flex justify-end"
            >
              <v-btn
                class="mt-5"
                fab
                dark
                small
                color="primary"
                @click="onExport(true)"
              >
                <v-icon dark>
                  mdi-download
                </v-icon>
              </v-btn>
              <v-btn
                class="mt-5 ml-1"
                fab
                dark
                small
                color="primary"
                @click="onExport()"
              >
                <v-icon dark>
                  mdi-eye
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="disabled"
            width="100px"
            text
            @click="closeModal"
          >
            Close
          </v-btn>
          <v-btn
            v-else
            type="submit"
            color="primary"
            width="200px"
          >
            Submit and Continue
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
    <v-dialog
      v-model="fileViewer.show"
      :fullscreen="fileViewer.maximize"
      persistent
    >
      <v-card min-height="700">
        <v-toolbar
          dark
          color="primary"
        >
          <v-toolbar-title>Initial Order</v-toolbar-title>
          <v-spacer />
          <v-toolbar-title>{{ attachmentName }}</v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            dark
            @click="fileViewer.maximize = !fileViewer.maximize"
          >
            <v-icon>{{ fileViewer.maximize ? 'mdi-window-restore' : 'mdi-window-maximize' }}</v-icon>
          </v-btn>
          <v-btn
            icon
            dark
            @click="fileViewer.show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-progress-linear
          v-show="fileViewer.loading"
          indeterminate
        />
        <iframe
          v-if="fileViewer.show && !fileViewer.loading"
          id="file-viewer"
          :style="`height:${fileViewer.maximize ? '100' : '80'}vh;`"
          :src="fileViewer.src + '#toolbar=0&navpanes=0'"
          frameborder="0"
          loading="lazy"
        />
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import crud from '@/utils/rest/crud'
import { numberToWords, monthName } from '@/utils/helpers'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
pdfMake.vfs = pdfFonts.pdfMake.vfs

export default {
  name: 'ProvideDetailsModal',
  props: {
    activeTab: {
      type: Number,
      default: 1
    },
    formItem: {
      type: Object,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: true
    },
    isSubmitted: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    modal: false,
    fileViewer: {
      show: false,
      fileName: '',
      src: '',
      maximize: false,
      loading: false
    }
  }),
  computed: {
    listItems () {
      return [
        {
          label: '1.a How breach occurred + DPS vulnerability',
          details: 'howBreachOccurred'
        },
        {
          label: '1.b Chronology',
          details: 'chronology'
        },
        {
          label: '1.c Number of DS / Records',
          details: 'numberOfDataSubjects'
        },
        {
          label: '1.d Description / Nature',
          details: 'descriptionOrNature'
        },
        {
          label: '1.e Likely Consequences',
          details: 'likelyConsequences'
        },
        {
          label: '1.f DPO',
          details: 'dataProtectionOfficer'
        },
        {
          label: '2.a SPI',
          details: 'sensitivePersonalInformation'
        },
        {
          label: '2.b Other info that may enable identity fraud',
          details: 'identityFraud'
        },
        {
          label: '3.a Measures to address the breach',
          details: 'measuresToAddress'
        },
        {
          label: '3.b Measures to secure/recover personal data?',
          details: 'measuresToSecure'
        },
        {
          label: '3.c Actions to mitigate harm?',
          details: 'actionsToMitigateHarm'
        },
        {
          label: '3.d Actions to inform data subjects?',
          details: 'actionsToInformDataSubjects'
        },
        {
          label: '3.e Measures to prevent recurrence of incidence',
          details: 'measuresToPreventRecurrence'
        }
      ]
    },
    noInfoYet () {
      const noInfoYet = []
      for (const item of this.listItems) {
        const key = item.details
        const provided = this.formItem[`${key}Provided`]
        if (provided === 'No information yet' || provided === 'No') {
          const label = item.label
          const model = item.details
          noInfoYet.push({ label, model, provided })
        }
      }
      if (!noInfoYet.length) this.$emit('update', { hasNoInfoYet: false })
      return noInfoYet
    },
    initialVal () {
      return field => {
        return this.formItem.noInformation && this.formItem.noInformation[`${field}`]
      }
    },
    hasNoInfoYet () {
      return this.noInfoYet.length > 0
    },
    attachmentName () {
      return `${this.formItem.dbnNumber.replace(/ /g, '_')}_initial_order.pdf`
    }
  },
  watch: {
    'fileViewer.show' (val) {
      if (!val) {
        window.URL.revokeObjectURL(this.fileViewer.src)
        this.fileViewer.src = ''
        this.fileViewer.maximize = false
      }
    }
  },
  beforeDestroy () {
    if (this.fileViewer.src) {
      window.URL.revokeObjectURL(this.fileViewer.src)
    }
  },
  created () {
    this.modal = !this.isSubmitted
  },
  methods: {
    convertDate (date) {
      if (!date) return
      const d = new Date(date)
      return `${d.getDate()} ${monthName(d.getMonth())} ${d.getFullYear()}`
    },
    // Close the modal
    closeModal () {
      this.modal = false
      this.$refs.form.resetValidation()
      if (this.isSubmitted) return
      for (const item of this.$refs.form.inputs) {
        const isAutocomplete = item.value === 'No information yet' || item.value === 'No'
        if (isAutocomplete) continue
        item.reset()
      }
    },
    // Shows confirmation dialog
    confirm (e) {
      const isValid = this.$refs.form.validate()
      if (!isValid) return
      this.$store.dispatch('showDialog', {
        title: 'Submit Details',
        titleColor: 'green',
        message: 'Are you sure you want to submit this form?',
        buttonLabel: 'Submit',
        buttonColor: 'green',
        callback: () => { this.submit(e) }
      })
    },
    // Submit provided details
    async submit (e) {
      const noInformation = Object.fromEntries(new FormData(e.target))
      const resp = await crud('pdbnf').update(this.formItem._id, { noInformation })
      if (!resp.ok) return
      await this.$emit('update', noInformation)
      this.closeModal()
    },
    // Export Initial Order
    async onExport (download = false) {
      const { initialOrderTemplate, formatListOfAttachments, computeNewLines } = (await import('@/meta/initialOrderLetter.js')).default
      const { formattedList, listCount } = formatListOfAttachments(this.formItem.enabled, this.formItem.others)
      const { hasApprovedRequestForExtension, extensionDays, daysForDocumentSubmission } = this.formItem
      const numberOfDays = hasApprovedRequestForExtension ? Number(extensionDays) + daysForDocumentSubmission : daysForDocumentSubmission
      const { date: initialOrderDate } = this.formItem.statusHistory.find(item => item.status === 'Initial Order')
      const initialOrderDetails = {
        picName: this.formItem.fullName,
        chiefName: this.formItem.assignedTo.final.fullName,
        enabledArr: formattedList,
        dbnNumber: this.formItem.dbnNumber,
        days: numberOfDays,
        daysString: numberToWords(numberOfDays, 'lowercased'),
        address: this.formItem.settingsAddress,
        daysText: numberOfDays > 1 ? 'days' : 'day',
        date: this.convertDate(initialOrderDate),
        formattedDateOfNotification: this.convertDate(this.formItem.dateOfNotification),
        formattedDateOfDiscovery: this.convertDate(this.formItem.dateOfDiscovery),
        email: this.formItem.email,
        newLines: computeNewLines(13, listCount, 19)
      }
      const docDefinition = await initialOrderTemplate(initialOrderDetails)
      pdfMake.createPdf(docDefinition).getBlob((blob) => {
        const url = URL.createObjectURL(blob)
        const fv = this.fileViewer
        if (download) {
          const link = document.createElement('a')
          link.style.display = 'none'
          link.href = url
          link.download = this.attachmentName
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(url)
          link.parentNode.removeChild(link)
        } else {
          fv.show = true
          fv.loading = true
          fv.fileName = this.attachmentName
          fv.src = url
          fv.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.highlight >>> fieldset {
  background-color: pink
}
.cursor >>>  textarea{
  overflow-y: hidden !important;
  cursor: default !important
}
#file-viewer {
  display: block;
  width: 100vw;
  max-width: 100%;
}
</style>
